import styled from 'styled-components'
import { screenSizes } from '@shipt/design-system-themes'
import { Image } from '@/features/shared/elements/Image/NextImage'
import {
  type AstroButton as AstroButtonType,
  type CTABannerData,
} from '@/features/cms/components/CMS/types'
import { Body, Display, Headline } from '@shipt/design-system-typography'
import { GridLayoutContainer } from '@/features/shared/styles/layout'
import { getCMSWrapperProps } from '@/features/cms/components/CMS/utils/getCMSWrapperProps'
import { ReferenceRenderer } from '@/features/cms/components/CMS/ReferenceRenderer'
import { Column, Stack } from '@shipt/design-system-layouts'
import { CMSErrorHandler } from '@/features/cms/components/CMS/CMSErrorHandler'
import isEmpty from 'lodash/isEmpty'
import { sanitizeHTML } from '@/features/shared/utils/sanitize'
import { useCMSTrackingEvents } from '@/features/cms/components/CMS/CMSContext'
import { renderElement } from '@/features/cms/components/CMS/RichTextRenderer'
import { ButtonLink } from '@/features/shared/elements/Link/Buttons'
import { getHeadingElement } from '@/features/shared/utils/getHeadingElement'
import { useIsPage } from '@/features/shared/hooks/useIsPage'
import { getRetailerUrl } from '@shared/constants/routes'
import { useSearchParams } from 'next/navigation'
import { theme } from '@/features/shared/constants/shiptTheme'

/**
 * This component corresponds to the `cta_banner` content type in the CMS.
 *
 * - **Staging** - https://cms-staging.shipt.com/content-type/pool/cta_banner
 * - **Production** - https://cms.shipt.com/content-type/pool/cta_banner
 */
export const CTABanner = (props: CTABannerData) => {
  const {
    content_type_id,
    id,
    data: {
      heading: headingText,
      subheading,
      subheading_rich_text,
      image,
      cta_text,
      references,
      style_variant,
      cta_button,
      seo_heading_level,
    },
  } = props
  const { trackCMSElementClicked } = useCMSTrackingEvents()
  const { TARGET_CIRCLE_360: isC360LP } = useIsPage()
  const searchParams = useSearchParams()
  const retailer_name = searchParams?.get('retailer_name')

  const handleButtonClick = () => {
    trackCMSElementClicked({
      content: cta_text?.text,
      type: 'button',
    })
  }
  const isRefresh = style_variant === 'refresh'

  const getAddedCTAUrl = () => {
    if (isC360LP && retailer_name) {
      return `&returnTo=${getRetailerUrl(String(retailer_name))}`
    }
    return ''
  }

  const getCTA = () => {
    if (cta_button?.data) {
      return (
        <AstroButton
          onClick={handleButtonClick}
          ctaButton={cta_button.data}
          appendedUrl={getAddedCTAUrl()}
        />
      )
    } else if (cta_text) {
      return (
        <ButtonLink
          href={`${cta_text.url}${getAddedCTAUrl()}`}
          onClick={handleButtonClick}
          opensInNewTab={cta_text.open_in_new_tab}
        >
          {cta_text.text}
        </ButtonLink>
      )
    }
  }

  try {
    return (
      <GridLayoutContainer {...getCMSWrapperProps({ content_type_id, id })}>
        <Column align="center">
          <ContentWrapper spacing="lg" align="center" isRefresh={isRefresh}>
            {!!image?.src && (
              <ImageWrapper>
                <Image src={image.src} width={75} height={75} alt={image.alt} />
              </ImageWrapper>
            )}
            {isRefresh ? (
              <NewHeadline align="center" as={getHeadingElement(seo_heading_level)} size="sm">
                {headingText}
              </NewHeadline>
            ) : (
              <Display align="center" as={getHeadingElement(seo_heading_level)} size="sm">
                {headingText}
              </Display>
            )}
            {!!subheading_rich_text && (
              <RichTextSubheading align="center">
                {subheading_rich_text.elements.map(renderElement)}
              </RichTextSubheading>
            )}
            {!!subheading && !subheading_rich_text && (
              <Subheading
                align="center"
                dangerouslySetInnerHTML={{ __html: sanitizeHTML(subheading) }}
                size={isRefresh ? { base: 'lg', sm: 'xl' } : 'lg'}
              />
            )}
            {isEmpty(references) ? null : <ReferenceRenderer references={references} />}
            {getCTA()}
          </ContentWrapper>
        </Column>
      </GridLayoutContainer>
    )
  } catch (error) {
    return <CMSErrorHandler error={error} contentTypeId={content_type_id} id={id} />
  }
}

type AstroButtonProps = {
  ctaButton: AstroButtonType['data']
  onClick: () => void
  appendedUrl: string
}

const AstroButton = ({ ctaButton, onClick, appendedUrl }: AstroButtonProps) => {
  const leadingIcon = ctaButton.icon_position === 'leading'
  const trailingIcon = ctaButton.icon_position === 'trailing'

  const getIcon = () => (ctaButton.icon ? <ButtonImage buttonImage={ctaButton.icon} /> : null)

  const updatedLink = () => {
    if (ctaButton.link?.url) {
      return `${ctaButton.link.url}${appendedUrl}`
    }
    return ''
  }

  return (
    <ButtonLink
      href={updatedLink()}
      onClick={onClick}
      opensInNewTab={ctaButton.link?.open_in_new_tab}
      surface={ctaButton.surface}
      variant={ctaButton.variant}
      size={{ base: 'md', sm: 'lg' }}
      leadingIcon={leadingIcon ? getIcon : undefined}
      trailingIcon={trailingIcon ? getIcon : undefined}
    >
      {ctaButton.text}
    </ButtonLink>
  )
}

const ButtonImage = ({ buttonImage }: { buttonImage: AstroButtonType['data']['icon'] }) => {
  if (!buttonImage) return

  return (
    <Image
      alt={buttonImage.alt}
      src={buttonImage.src}
      height={18}
      width={99}
      style={{ objectFit: 'contain' }}
    />
  )
}

const ImageWrapper = styled.div`
  display: flex;
  justify-content: center;
`

const ContentWrapper = styled(Column)<{ isRefresh: boolean }>`
  @media ${screenSizes.tablet} {
    width: ${({ isRefresh }) => (isRefresh ? '80%' : '50%')};
  }
`

const Subheading = styled(Body)`
  white-space: pre-wrap;
`

const RichTextSubheading = styled(Stack)`
  & p {
    color: ${theme.color.content.base.neutral.secondary};
  }
`
const NewHeadline = styled(Headline)`
  font-size: 1.875rem;
  line-height: 2.125rem;

  @media ${screenSizes.tablet} {
    font-size: 4rem;
    line-height: 4.5rem;
  }
`
